import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Row, Col, Button, Form } from 'antd';
import { useSelector } from 'react-redux';
import api, {
	DLCAssetsType,
	DownloadableContentAsset,
	UpdateDownloadableContentAssetItemsRequest,
} from 'api';
import { getDlcAssettems } from 'utils/productUtils';
import OmniNotification from 'components/OmniNotification';
import * as yup from 'yup';
import {
	Controller,
	FieldValues,
	FormProvider,
	Path,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	FileModel,
	FileYupSchema,
	generateUploadFileList,
	GetUploadLink,
	ImageFileModel,
	ValidateS3Keys,
	isVideoExactResolutionValid,
	isFileFormatValid,
} from 'utils/fileUtils';
import ImageFileUpload from 'components/ImageFileUpload';
import FileUpload from 'components/FileUpload';
import { focusFirstErrorField } from 'utils/validationUtils';
import { min, isEmpty } from 'lodash';
import styles from './Assets.module.less';
import { projectSelector } from 'redux/selectors/projectSelector';
import {
	DLCTabName,
	IDownloadableContentDetailDto,
} from './EditDownloadableContent';

const DETAILIMAGES_LENGTH = 7;
const DETAILIMAGES_REQUIRED_LENGTH = 4;
const VIDEO_EXACT_RESOLUTION = {
	width: 1280,
	height: 720,
};
const VIDEO_ALLOWED_FORMATS = ['.mp4'];

interface FormValues extends FieldValues {
	coverArt: ImageFileModel | null;
	icon: ImageFileModel | null;
	detailImage1: ImageFileModel | null;
	detailImage2: ImageFileModel | null;
	detailImage3?: ImageFileModel | null | undefined;
	detailImage4?: ImageFileModel | null | undefined;
	detailImage5?: ImageFileModel | null | undefined;
	detailImage6?: ImageFileModel | null | undefined;
	video1: FileModel[] | null | undefined;
	video1Image: ImageFileModel | null;
}
const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		coverArt: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t(
						'downloadableContentEdit.tabs.assets.form.coverArt.title',
					),
				}),
			),
		icon: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContentEdit.tabs.assets.form.icon.title'),
				}),
			),
		detailImage1: yup.object(FileYupSchema).nullable(),
		detailImage2: yup.object(FileYupSchema).nullable(),
		detailImage3: yup.object(FileYupSchema).nullable(),
		detailImage4: yup.object(FileYupSchema).nullable(),
		video1: yup
			.array()
			.of(yup.object(FileYupSchema))
			.test('ValidateS3Keys', '', (value) => {
				if (!value || value.length === 0) {
					return true;
				}
				return ValidateS3Keys(value);
			}),
		video1Image: yup.object(FileYupSchema).nullable(),
	});

const defaultValues: FormValues = {
	coverArt: null,
	icon: null,
	detailImage1: null,
	detailImage2: null,
	detailImage3: null,
	detailImage4: null,
	detailImage5: null,
	detailImage6: null,
	video1: undefined,
	video1Image: null,
};

type Props = {
	id: number;
	data: IDownloadableContentDetailDto;
	onSave: (number: string) => void;
};

export interface AssetsFormRef {
	validateAssetsFormTab: () => void;
}

export default forwardRef(function AssetsForm(props: Props, ref: any) {
	const { t } = useTranslation();
	const projectInfo = useSelector(projectSelector);
	const { id, data, onSave } = props;
	const validationSchema = getValidationSchema(t);
	const [
		shouldCallSubmitWhenNavigatingAway,
		setShouldCallSubmitWhenNavigatingAway,
	] = useState(false);
	const [imageValues, setImageValues] = useState<FormValues>();
	const [isImage1Loading, setIsImage1Loading] = useState(false);
	const [isVideo1Loading, setIsVideo1Loading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const pageForm = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});
	const [timesOfLoadData, setTimesOfLoadData] = useState<number>(0);
	const manuallySave = useRef(false);
	const {
		control,
		handleSubmit,
		watch,
		trigger,
		reset,
		setError,
		formState,
		setFocus,
	} = pageForm;

	useImperativeHandle(ref, () => ({
		async validateAssetsFormTab() {
			const isValid = await preValidateForm();
			if (!isValid) {
				return isValid;
			}
			return await trigger();
		},
	}));

	const video1FormValue = watch('video1');
	const video1ImageFormValue = watch('video1Image');

	useEffect(() => {
		if (
			(data && data.assets && data.assets!.length > 0 && isSubmitting) ||
			timesOfLoadData == 0
		) {
			var assets = data.assets?.filter((item) => {
				return item.type != DLCAssetsType.File;
			});
			const galleryImages = convertGalleryToPlain2(assets);
			setImageValues(galleryImages);
			const values = {
				...galleryImages,
			};
			reset(values);
		}

		if (
			!isEmpty(data) &&
			(!data.tabName || data.tabName == DLCTabName.Assets)
		) {
			setTimesOfLoadData(timesOfLoadData + 1);
		}
	}, [data]);

	useEffect(() => {
		if (timesOfLoadData > 1) {
			setIsSubmitting(false);
		}
	}, [timesOfLoadData]);

	const convertGalleryToPlain2 = (
		galleryItems: DownloadableContentAsset[] | undefined,
	): FormValues => {
		if (!galleryItems?.length)
			return {
				coverArt: null,
				icon: null,
				detailImage1: null,
				detailImage2: null,
				detailImage3: null,
				detailImage4: null,
				detailImage5: null,
				detailImage6: null,
				detailImage7: null,
				video1: undefined,
				video1Image: null,
			};

		const coverCoverArt = getDlcAssettems(
			galleryItems,
			DLCAssetsType.CoverArt,
		)?.[0];
		const coverSquareImage = getDlcAssettems(
			galleryItems,
			DLCAssetsType.Icon,
		)?.[0];
		const detailImages = getDlcAssettems(
			galleryItems,
			DLCAssetsType.DetailImages,
		);
		const video1 = getDlcAssettems(galleryItems, DLCAssetsType.Video)[0];

		const video1Image = getDlcAssettems(
			galleryItems,
			DLCAssetsType.VideoScreenshot,
		)[0];

		const detailImageImages = Array.from(
			Array(min([DETAILIMAGES_LENGTH, detailImages.length])),
			(_, index) => index,
		)
			.map((index) => ({
				fileName: detailImages[index].name as string,
				s3Key: detailImages[index].key as string,
				imageSrc: detailImages[index].url,
			}))
			.reduce((a, v, i) => ({ ...a, [`detailImage${i + 1}`]: v }), {});

		const restImages = {
			coverArt: coverCoverArt
				? {
						fileName: coverCoverArt.name as string,
						s3Key: coverCoverArt.key as string,
						imageSrc: coverCoverArt.url,
					}
				: null,
			icon: coverSquareImage
				? {
						fileName: coverSquareImage.name as string,
						s3Key: coverSquareImage.key as string,
						imageSrc: coverSquareImage.url,
					}
				: null,
			video1: video1
				? [
						{
							fileName: video1.name as string,
							s3Key: video1.key as string,
							imageSrc: video1.url,
						},
					]
				: undefined,
			video1Image: video1Image
				? {
						fileName: video1Image.name as string,
						s3Key: video1Image.key as string,
						imageSrc: video1Image.url,
					}
				: null,
		};

		return { ...restImages, ...detailImageImages } as FormValues;
	};

	const preValidateForm = async () => {
		const image1WithoutVideo = video1ImageFormValue && !video1FormValue?.length;
		const video1WithoutImage = !video1ImageFormValue && video1FormValue?.length;

		if (image1WithoutVideo || video1WithoutImage) {
			await trigger(undefined);

			if (image1WithoutVideo) {
				setError('video1', {
					message: t('validation.required', {
						fieldName: t('downloadableContentEdit.tabs.assets.error.video'),
					}),
				});
			}

			if (video1WithoutImage) {
				setError('video1Image', {
					message: t('validation.required', {
						fieldName: t('addProductPage.Video1image'),
					}),
				});
			}

			focusFirstErrorField({
				setFocus,
				errors: formState.errors,
				fields: Object.keys(validationSchema.fields),
			});

			return false;
		}

		return true;
	};

	const onSubmit = async (values: FormValues) => {
		const request: UpdateDownloadableContentAssetItemsRequest = {
			productId: projectInfo?.productId,
			assetItems: [
				{
					dlcId: id,
					type: DLCAssetsType.Icon,
					name: values.icon?.fileName,
					key: values.icon?.s3Key,
				},
				{
					dlcId: id,
					type: DLCAssetsType.CoverArt,
					name: values.coverArt?.fileName,
					key: values.coverArt?.s3Key,
				},
			],
		};

		if (values.video1?.length) {
			request.assetItems?.push({
				dlcId: id,
				type: DLCAssetsType.Video,
				name: values.video1[0].fileName,
				key: values.video1[0].s3Key,
			});

			request.assetItems?.push({
				dlcId: id,
				type: DLCAssetsType.VideoScreenshot,
				name: values.video1Image?.fileName,
				key: values.video1Image?.s3Key,
			});
		}

		Array.from(Array(DETAILIMAGES_LENGTH), (_, index) => index).forEach(
			(index) => {
				const detailImageKey = `detailImage${index + 1}` as keyof FormValues;
				const detailImage = values[detailImageKey];
				if (detailImage) {
					request.assetItems?.push({
						dlcId: id,
						type: DLCAssetsType.DetailImages,
						name: (detailImage as ImageFileModel).fileName,
						key: (detailImage as ImageFileModel).s3Key,
					});
				}
			},
		);

		setIsSubmitting(true);
		api
			.updateAssetsForDLC(id, request)
			.then(() => {
				OmniNotification.success({
					message: t('downloadableContentEdit.message.update.success.title'),
					description: t(
						'downloadableContentEdit.message.update.success.description',
					),
				});

				onSave(DLCTabName.Assets);
			})
			.catch(() => {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: t('api.defaultError'),
				});
				setIsSubmitting(false);
			});
	};

	const handleFinish = async () => {
		const isValid = await preValidateForm();
		isValid && (await handleSubmit(onSubmit)());
	};

	const beforeUploadVideoValidation = async (file: File) => {
		const formatVailid = isFileFormatValid(file, VIDEO_ALLOWED_FORMATS);
		if (!formatVailid) {
			return {
				success: false,
				errorMessage: t('uploader.fileTypeError'),
			};
		}
		const valid = await isVideoExactResolutionValid(
			file,
			VIDEO_EXACT_RESOLUTION,
		);
		if (valid) {
			return { success: true };
		} else {
			return {
				success: false,
				errorMessage: t('uploader.videoExactResolutionError', {
					...VIDEO_EXACT_RESOLUTION,
				}),
			};
		}
	};

	useEffect(() => {
		if (shouldCallSubmitWhenNavigatingAway) {
			manuallySave.current = false;
			handleSubmit(onSubmit)();
		}
	}, [shouldCallSubmitWhenNavigatingAway]);

	return (
		<>
			<FormProvider {...pageForm}>
				<Form
					data-testid="form"
					layout="vertical"
					onFinish={handleFinish}
					onFinishFailed={preValidateForm}
				>
					<div className="scroll-box">
						<Row>
							<div style={{ flexDirection: 'row', alignItems: 'center' }}>
								<span className={styles.title}>
									{t('downloadableContentEdit.tabs.assets.title')}
								</span>
								<span className={styles.description}>
									{t('downloadableContentEdit.tabs.assets.description')}
								</span>
							</div>
						</Row>
						<Row style={{ marginTop: 8 }}>
							<Col span={24}>
								<div
									style={{
										display: 'flex',
										flexFlow: 'row wrap',
										marginBottom: '24px',
									}}
								>
									<div
										style={{
											display: 'block',
											flexGrow: 0,
											flexShrink: 0,
											flexBasis: '36%',
											maxWidth: '36%',
										}}
									>
										<div className="uploader uploader--sm">
											<div
												style={{
													marginBottom: 4,
													fontSize: 16,
												}}
											>
												{t(
													'downloadableContentEdit.tabs.assets.form.icon.title',
												)}
											</div>
											<div
												style={{
													marginBottom: 8,
													minHeight: '50px',
												}}
											>
												{t(
													'downloadableContentEdit.tabs.assets.form.icon.description',
												)}
											</div>
											<Controller
												name="icon"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														customSuccessCallback={() => {
															OmniNotification.success({
																message: t(
																	'downloadableContentEdit.message.upload.success.title',
																),
																description: t(
																	'downloadableContentEdit.message.upload.success.description',
																),
															});
														}}
														defaultFileList={generateUploadFileList(
															imageValues?.icon,
														)}
														defaultImageUrl={imageValues?.icon?.imageSrc}
														field={field as any}
														exactResolution={{
															width: 512,
															height: 512,
														}}
													/>
												)}
											/>
											<div className="uploader__hint">
												{t(
													'downloadableContentEdit.tabs.assets.form.icon.hint',
												)}
											</div>
										</div>
									</div>
									<div
										style={{
											display: 'block',
											flexGrow: 0,
											flexShrink: 0,
											flexBasis: '64%',
											maxWidth: '64%',
										}}
									>
										<div
											style={{
												paddingLeft: '2%',
											}}
											className="uploader uploader--md aspect16to9"
										>
											<div
												style={{
													marginBottom: 4,
													fontSize: 16,
												}}
											>
												{t(
													'downloadableContentEdit.tabs.assets.form.coverArt.title',
												)}
											</div>
											<div
												style={{
													marginBottom: 8,
													height: '50px',
												}}
											>
												{t(
													'downloadableContentEdit.tabs.assets.form.coverArt.description',
												)}
											</div>
											<Controller
												name="coverArt"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														customSuccessCallback={() => {
															OmniNotification.success({
																message: t(
																	'downloadableContentEdit.message.upload.success.title',
																),
																description: t(
																	'downloadableContentEdit.message.upload.success.description',
																),
															});
														}}
														defaultFileList={generateUploadFileList(
															imageValues?.coverArt,
														)}
														defaultImageUrl={imageValues?.coverArt?.imageSrc}
														field={field as any}
														exactResolution={{
															width: 640,
															height: 360,
														}}
													/>
												)}
											/>
											<div className="uploader__hint">
												{t(
													'downloadableContentEdit.tabs.assets.form.coverArt.hint',
												)}
											</div>
										</div>
									</div>
								</div>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<div
									style={{
										marginBottom: 4,
										fontSize: 16,
									}}
								>
									{t(
										'downloadableContentEdit.tabs.assets.form.detailImages.title',
									)}
								</div>
								<div
									style={{
										marginBottom: 8,
									}}
								>
									{t(
										'downloadableContentEdit.tabs.assets.form.detailImages.description',
									)}
								</div>
								<Row gutter={[24, 0]}>
									{Array.from(
										Array(DETAILIMAGES_LENGTH),
										(_, index) => index,
									).map((index) => {
										const name = `detailImage${index + 1}`;
										return (
											<Col
												span={8}
												key={index}
												style={{ marginTop: '8px', marginBottom: '8px' }}
											>
												<div className="uploader uploader--md aspect16to9">
													<Controller
														name={name as Path<FormValues>}
														control={control}
														render={({ field, fieldState }) => (
															<ImageFileUpload
																error={fieldState.error}
																className="uploader__box"
																buttonText={''}
																uploadLinkFunction={GetUploadLink}
																customSuccessCallback={() => {
																	OmniNotification.success({
																		message: t(
																			'downloadableContentEdit.message.upload.success.title',
																		),
																		description: t(
																			'downloadableContentEdit.message.upload.success.description',
																		),
																	});
																}}
																onImageChange={() => {
																	if (
																		field.name === 'detailImage1' ||
																		field.name === 'detailImage2' ||
																		field.name === 'detailImage4' ||
																		field.name === 'detailImage4'
																	) {
																		return;
																	}
																	trigger('detailImage1');
																	trigger('detailImage2');
																	trigger('detailImage3');
																	trigger('detailImage4');
																}}
																defaultFileList={generateUploadFileList(
																	(imageValues as any)?.[name],
																)}
																defaultImageUrl={
																	(imageValues as any)?.[name]?.imageSrc
																}
																field={field as any}
																exactResolution={{
																	width: 1280,
																	height: 720,
																}}
															/>
														)}
													/>
													<div className="uploader__caption">
														{t(
															'downloadableContentEdit.tabs.assets.form.detailImages.subTitle',
															{
																detailImageNum: index + 1,
															},
														)}{' '}
														{index + 1 > DETAILIMAGES_REQUIRED_LENGTH &&
															t(
																'downloadableContentEdit.tabs.assets.form.detailImages.optional',
															)}
													</div>
													<div className="uploader__hint">
														{t(
															'downloadableContentEdit.tabs.assets.form.detailImages.hint',
														)}
													</div>
												</div>
											</Col>
										);
									})}
								</Row>
							</Col>
							<Col span={24}>
								<div
									style={{
										marginTop: 8,
										marginBottom: 4,
										fontSize: 16,
									}}
								>
									{t('downloadableContentEdit.tabs.assets.form.video.title')}
								</div>
								<div
									style={{
										marginBottom: 16,
									}}
								>
									{t(
										'downloadableContentEdit.tabs.assets.form.video.description',
									)}
								</div>
								<Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
									<Col span={8}>
										<div className="uploader__caption">
											{t(
												'downloadableContentEdit.tabs.assets.form.video.subTitle1',
											)}
										</div>
										<div className="uploader__hint">
											{t(
												'downloadableContentEdit.tabs.assets.form.video.hint1',
											)}
										</div>
									</Col>
									<Col span={6}>
										<Controller
											name="video1"
											control={control}
											render={({ field, fieldState }) => (
												<FileUpload
													defaultFileList={generateUploadFileList(
														imageValues?.video1,
													)}
													error={fieldState.error}
													uploadLinkFunction={GetUploadLink}
													field={field as any}
													customSuccessCallback={() => {
														OmniNotification.success({
															message: t(
																'downloadableContentEdit.message.upload.success.title',
															),
															description: t(
																'downloadableContentEdit.message.upload.success.description',
															),
														});
													}}
													acceptedFileFormat={VIDEO_ALLOWED_FORMATS}
													acceptedFileSize={100}
													buttonText={t(
														'addProductPage.videos.uploadVideoFile',
													)}
													beforeUploadValidation={beforeUploadVideoValidation}
													onLoadingChange={setIsVideo1Loading}
													onFileListChange={(files) => {
														if (!files.length) {
															setImageValues({
																...imageValues,
																video1: [],
															} as FormValues);
														}
														trigger('video1Image');
													}}
													className="uploader__btn"
												/>
											)}
										/>
									</Col>
								</Row>
								<Row
									gutter={[24, 24]}
									style={{ marginTop: '8px', marginBottom: '8px' }}
								>
									<Col span={14}>
										<div className="uploader uploader--md aspect16to9">
											<Controller
												name="video1Image"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														customSuccessCallback={() => {
															OmniNotification.success({
																message: t(
																	'downloadableContentEdit.message.upload.success.title',
																),
																description: t(
																	'downloadableContentEdit.message.upload.success.description',
																),
															});
														}}
														defaultFileList={generateUploadFileList(
															imageValues?.video1Image,
														)}
														defaultImageUrl={imageValues?.video1Image?.imageSrc}
														onLoadingChange={setIsImage1Loading}
														field={field as any}
														onImageChange={() => {
															trigger('video1');
														}}
														exactResolution={{
															width: 1280,
															height: 720,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">
												{t(
													'downloadableContentEdit.tabs.assets.form.video.subTitle2',
												)}
											</div>
											<div className="uploader__hint">
												{t(
													'downloadableContentEdit.tabs.assets.form.video.hint2',
												)}
											</div>
										</div>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>
					<Button
						data-testid="assets-tab-save-btn"
						type="primary"
						loading={isSubmitting}
						htmlType="submit"
						disabled={
							timesOfLoadData > 1 && (!formState.isDirty || isSubmitting)
						}
						size="large"
					>
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
});
